import {
	DateObject,
	isPwaMode,
	NotificationService,
	TrackingEventTypeWEB,
	useIntl,
	UserCredentials,
	useSettings
} from '@capsicum/common';
import { Alert, Button, Label, Snackbar, SsuHud, Timer, useIdParam, useRouter } from '@capsicum/common-web';
import { AccessIcon, BundleTicketIcon, InfoIcon, ReverseAuctionIcon } from '@capsicum/common-web/icons';
import { setProductQuantityAtom, useProductPage } from '@capsicum/features/fanshop/shared';
import { ProductDetailsSkeleton } from '@capsicum/features/fanshop/web';
import { ApprovedFooter } from '@capsicum/features/regulations/web';
import { RequirementsPageCredentialsNeeded, useNCWBalance, usePartners } from '@capsicum/socios-core';
import { useBuySellButtons, useCredentialsHandler } from '@capsicum/socios-core-web';
import { useSetAtom } from 'jotai';
import { type ComponentProps, type ReactNode, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTheme } from 'styled-components';

import * as S from './Product.styled';

export type ProductProps = {
	params: { id?: string };
};

export const Product = ({ params }: ProductProps) => {
	// Core hooks
	const { colors, sizing } = useTheme();
	const router = useRouter();
	const { $t } = useIntl();
	const { getTokenBalanceBy, isPending: isPendingNCWallet } = useNCWBalance();
	// To reset the quantity when the user navigates back to the product page
	const setProductQuantity = useSetAtom(setProductQuantityAtom);

	// Custom hooks
	const { id } = useIdParam(params, '/fan-shop/rewards');

	const {
		allImages,
		countDownString,
		eventDate,
		faqLink,
		getCategory,
		getSsuAmount,
		hasEnoughFanTokens,
		hasEnoughStock,
		hasEventDate,
		hasLinkedProduct,
		hasLinkedProductOrder,
		hasReachedOrderLimit,
		importantInformationTranslationsArray,
		isComingSoon,
		isExperienceType,
		isFetching,
		isLoading,
		isOutOfStock,
		isPackageProduct,
		isPendingCWallet,
		isPhysicalTicket,
		isRedeemEnabled,
		isReverseAuction,
		isTicketType,
		matchPlace,
		packageProductPrice,
		packageQuantity,
		productDescription,
		sendEventAnalytics,
		setIsComingSoon,
		setShowLimitError,
		showLimitError,
		texts,
		variant
	} = useProductPage({ getTokenBalanceBy, variantId: id as string });

	useEffect(() => {
		setProductQuantity(0);
	}, []);

	useEffect(() => {
		if (variant?.product?.totalUserProductPurchases && hasReachedOrderLimit && showLimitError) {
			setShowLimitError(false);
			NotificationService.addNotification({
				component: (
					<Snackbar
						description={texts.limitExceededError}
						testID="product-limitExceeded-error"
						variant="error"
					/>
				)
			});
		}
	}, [showLimitError, hasReachedOrderLimit, variant]);

	const { getPartnerBy } = usePartners();
	const currentPartner = useMemo(
		() => getPartnerBy(p => p.id === variant?.product?.partnerId),
		[variant?.product?.partnerId, getPartnerBy]
	);
	const { buy } = useBuySellButtons(currentPartner?.currencyId || '');

	const { handleCredential: onRedeem } = useCredentialsHandler({
		credentialCodes: UserCredentials.FAN_SHOP_ACCESS,
		onCredentialGranted: () => {
			sendEventAnalytics(TrackingEventTypeWEB.track, {
				is_pwa_mode: isPwaMode(),
				page: 'Product Details'
			});
			setShowLimitError(false);
			router.push(`/fan-shop/rewards/order-summary/${id}`);
		},
		page: RequirementsPageCredentialsNeeded.productDetails
	});

	const isPendingCombinedWallets = !isPendingCWallet && !isPendingNCWallet;

	const [activeImage, setActiveImage] = useState<number>(0);

	const onThumbnailChange = (index: number) => {
		setActiveImage(index);
	};

	const imageWrapper = useMemo(
		() => (
			<S.ImageWrapper>
				<S.ImageContainer>
					{allImages?.[activeImage]?.url ? (
						<S.ProductImage
							alt={`${variant?.label} image`}
							blurhash={allImages?.[activeImage]?.blurHash}
							height={303}
							optimized
							src={allImages?.[activeImage]?.url || ''}
							width={256}
						/>
					) : (
						<S.StyledCardImagePlaceholder />
					)}
				</S.ImageContainer>
				{allImages?.length > 1 && (
					<S.GalleryView>
						{allImages?.map((image, index) => (
							<S.Pressable
								$selected={activeImage === index}
								data-testid={`galleryView-image${index}`}
								key={index}
								onClick={() => onThumbnailChange(index)}
							>
								<S.StyledImage
									alt={`${variant?.label}-image${index}`}
									blurhash={image?.blurHash}
									height={70}
									optimized
									src={image?.url || ''}
									width={90}
								/>
							</S.Pressable>
						))}
					</S.GalleryView>
				)}
			</S.ImageWrapper>
		),
		[allImages, variant?.label, activeImage, allImages?.[activeImage]?.url]
	);

	const timer = useMemo(() => {
		if (isComingSoon) {
			return (
				<S.ComingSoon>
					<S.LabelStatus>{texts.availableIn}</S.LabelStatus>
					<Timer
						customFormat="digitalTimer"
						expirationDate={new DateObject(variant?.redeemableAt || '')}
						size="condensed"
						type="default"
						onElapsed={() => setIsComingSoon(false)}
					/>
				</S.ComingSoon>
			);
		} else if (!variant?.redeemableAt && hasEnoughStock) {
			return <Timer customText="Coming soon" expirationDate={undefined} size="condensed" />;
		}
	}, [isComingSoon, texts.availableIn, variant?.redeemableAt]);

	const categoryInfo = useMemo(
		() => (
			<S.CategoryInfo>
				{(isExperienceType && hasEventDate) || isTicketType || isPhysicalTicket ? (
					<>
						<S.InfoWrapper>
							<S.DetailsTitle>{isExperienceType ? texts.eventDate : texts.matchDate}</S.DetailsTitle>
							<S.DetailsValue>{eventDate}</S.DetailsValue>
						</S.InfoWrapper>
						{isTicketType && (
							<div>
								<S.DetailsTitle>{texts.stadium}</S.DetailsTitle>
								<S.DetailsValue>{matchPlace}</S.DetailsValue>
							</div>
						)}
					</>
				) : (
					<div>
						<S.DetailsTitle>{texts.category}</S.DetailsTitle>
						<S.DetailsValue>{getCategory(variant)}</S.DetailsValue>
					</div>
				)}
			</S.CategoryInfo>
		),
		[
			isTicketType,
			isPhysicalTicket,
			texts,
			matchPlace,
			eventDate,
			getCategory,
			variant,
			isExperienceType,
			hasEventDate
		]
	);

	const { settings } = useSettings();
	const showTokenNeededLink = settings?.showTokenNeededLink;

	const informationBanner = useMemo(
		() => (
			<Alert
				alignIcon="flex-start"
				closable={false}
				icon={InfoIcon}
				subTitle={
					isTicketType || isExperienceType
						? $t('rewards.fanshop.linkedProductsBanner.suspendedTicketText')
						: $t('rewards.fanshop.linkedProductsBanner.suspendedDefaultText')
				}
				testID="product-information-banner"
				title={$t('rewards.fanshop.linkedProductsBanner.title')}
				type="important"
			/>
		),
		[$t, isTicketType, isExperienceType]
	);

	return (
		<S.Wrapper>
			<S.ProductUkHeaderWarning horizontalPadding page="rewardsProduct" verticalPadding />
			{isLoading && isFetching ? (
				<ProductDetailsSkeleton />
			) : (
				<>
					<S.CategoryTitle>{texts.title}</S.CategoryTitle>
					<S.ProductWrapper>
						{imageWrapper}
						<S.DetailsWrapper>
							{isOutOfStock && <S.OutOfStockText>{texts.outOfStock}</S.OutOfStockText>}
							<S.HeaderWrapper>
								<S.TitleWrapper>
									<S.Category>{getCategory(variant)}</S.Category>
									<S.Title>{variant?.label}</S.Title>
								</S.TitleWrapper>
								{timer}
							</S.HeaderWrapper>
							<S.LabelsContainer>
								<SsuHud testID="product-ssu-hud">
									{isPackageProduct ? packageProductPrice : getSsuAmount()}
								</SsuHud>
								{isReverseAuction && (
									<Label
										icon={(p: ComponentProps<typeof ReverseAuctionIcon>) => (
											<ReverseAuctionIcon width={sizing.xsmall} {...p} />
										)}
										label={countDownString}
										size="condensed"
										variant="outlined"
									/>
								)}
								{!hasEnoughFanTokens() && isPendingCombinedWallets && (
									<S.BuyTokensLabel>
										<AccessIcon color={colors.icons.main.enabled} width={sizing.xsmall} />
										<S.TokensNeededText>{texts.tokensNeeded}</S.TokensNeededText>
										{showTokenNeededLink && (
											<S.StyledTextButton
												color="brand"
												testID="product-buyFanToken-button"
												text={texts.buyFanTokens}
												textSize="small"
												onClick={buy}
											/>
										)}
									</S.BuyTokensLabel>
								)}
								{isPackageProduct && (
									<Label
										icon={(p: ComponentProps<typeof BundleTicketIcon>) => (
											<BundleTicketIcon width={sizing.xsmall} {...p} />
										)}
										label={`${packageQuantity} ${texts.tickets}`}
										size="condensed"
										variant="outlined"
									/>
								)}
							</S.LabelsContainer>
							{categoryInfo}
							{hasLinkedProductOrder && hasLinkedProduct && (
								<S.Divider>
									<S.InformationWrapper>{informationBanner}</S.InformationWrapper>
								</S.Divider>
							)}
							<div>
								<S.BlockTitle>{texts.details}</S.BlockTitle>
								<S.Description>{productDescription}</S.Description>
							</div>
							{!!importantInformationTranslationsArray.length && (
								<div>
									<S.BlockTitle $isMarginBottom>{texts.importantInformationTitle}</S.BlockTitle>
									<S.importantInformationList>
										{importantInformationTranslationsArray.map((text, index) => (
											<S.importantInformationItem key={`important-information-item-${index}`}>
												{text}
											</S.importantInformationItem>
										))}
									</S.importantInformationList>
								</div>
							)}
							<div>
								<S.BlockTitle $isMarginBottom>{texts.faqTitle}</S.BlockTitle>
								<S.Description>
									{$t('rewards.fanshop.product.faqDescription', {
										link: (chunks: Array<ReactNode>) => (
											<S.Link data-testid="faq-link" href={faqLink} target="_blank">
												{chunks}
											</S.Link>
										)
									})}
								</S.Description>
							</div>
							<S.ButtonWrapper>
								<Button
									disabled={!isRedeemEnabled || hasLinkedProductOrder}
									fullWidth={isMobile}
									testID="product-redeem-button"
									text={texts.redeemButton}
									onClick={onRedeem}
								/>
							</S.ButtonWrapper>
						</S.DetailsWrapper>
					</S.ProductWrapper>
				</>
			)}
			<ApprovedFooter page="rewardsProduct" />
		</S.Wrapper>
	);
};
